import { formatSupplementalNameEditPayload } from "@/services/helpers";
import { getSupplementalsByEntity } from "@/services/supplementals";
import type { DialogTabSaveContext } from "@/v2/framework/dialog/types";

export const OUTCOME_SUPPLEMENTAL_EDITS_KEY = "_outcomeSupplementalEdits";

export type OutcomeSupplementalEditRow = {
  supplementalNameTypeId: string | number;
  computedName: string | null;
  computedEventMarketOutcomeName: string | null;
  parentName?: string | null;
  parentEventMarketOutcomeName?: string | null;
  namePolicy?: number;
  eventMarketOutcomeNamePolicy?: number;
};

export function buildAddOutcomeSupplementalNames(
  values: Record<string, unknown>,
): Record<string, unknown>[] {
  return getSupplementalsByEntity("OUTCOME")
    .map((type) => {
      const name = values[`supplemental_${type.id}`];
      const eventMarketOutcomeName = values[`supplementalEvent_${type.id}`];
      return {
        supplementalNameTypeId: type.id,
        name: name == null || name === "" ? null : String(name),
        eventMarketOutcomeName:
          eventMarketOutcomeName == null || eventMarketOutcomeName === ""
            ? null
            : String(eventMarketOutcomeName),
      };
    })
    .filter((item) => item.name || item.eventMarketOutcomeName);
}

export function buildOutcomeSupplementalNamesPatch(
  values: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> | null {
  const edits = values[OUTCOME_SUPPLEMENTAL_EDITS_KEY];
  if (!edits || typeof edits !== "object" || Array.isArray(edits)) return null;

  const editRows = Object.values(edits as Record<string, OutcomeSupplementalEditRow>);
  if (!editRows.length) return null;

  const baselineEdits = baseline[OUTCOME_SUPPLEMENTAL_EDITS_KEY];
  const baselineMap =
    baselineEdits && typeof baselineEdits === "object" && !Array.isArray(baselineEdits)
      ? (baselineEdits as Record<string, OutcomeSupplementalEditRow>)
      : {};

  let supplementalNames: Record<string, unknown>[] | null = null;
  const supplementalPolicies: Record<string, { name?: number; eventMarketOutcomeName?: number }> =
    {};
  let hasChanges = false;

  for (const edit of editRows) {
    const key = String(edit.supplementalNameTypeId);
    const baselineRow = baselineMap[key];

    if ((baselineRow?.computedName ?? null) !== (edit.computedName ?? null)) {
      supplementalNames = formatSupplementalNameEditPayload({
        supplementalNameList: supplementalNames,
        supplementalNameTypeId: edit.supplementalNameTypeId,
        value: edit.computedName,
        key: "name",
      }) as Record<string, unknown>[];
      hasChanges = true;
    }

    if (
      (baselineRow?.computedEventMarketOutcomeName ?? null) !==
      (edit.computedEventMarketOutcomeName ?? null)
    ) {
      supplementalNames = formatSupplementalNameEditPayload({
        supplementalNameList: supplementalNames,
        supplementalNameTypeId: edit.supplementalNameTypeId,
        value: edit.computedEventMarketOutcomeName,
        key: "eventMarketOutcomeName",
      }) as Record<string, unknown>[];
      hasChanges = true;
    }

    if ((baselineRow?.namePolicy ?? 1) !== (edit.namePolicy ?? 1)) {
      supplementalPolicies[key] = { ...supplementalPolicies[key], name: edit.namePolicy ?? 1 };
      hasChanges = true;
    }

    if (
      (baselineRow?.eventMarketOutcomeNamePolicy ?? 1) !== (edit.eventMarketOutcomeNamePolicy ?? 1)
    ) {
      supplementalPolicies[key] = {
        ...supplementalPolicies[key],
        eventMarketOutcomeName: edit.eventMarketOutcomeNamePolicy ?? 1,
      };
      hasChanges = true;
    }
  }

  if (!hasChanges) return null;

  const patch: Record<string, unknown> = {};
  if (supplementalNames) patch.supplementalNames = supplementalNames;
  if (Object.keys(supplementalPolicies).length) {
    patch.sourcePolicyMap = { supplementalNames: supplementalPolicies };
  }
  return patch;
}

export function syncOutcomeSupplementalBaseline(ctx: DialogTabSaveContext): void {
  const edits = ctx.values[OUTCOME_SUPPLEMENTAL_EDITS_KEY];
  if (edits && typeof edits === "object" && !Array.isArray(edits)) {
    ctx.baseline[OUTCOME_SUPPLEMENTAL_EDITS_KEY] = JSON.parse(JSON.stringify(edits));
  }
}
